import { compositeDisposable, IDisposable } from "../../utils/disposable";
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	hasClass,
} from "../../common/html";

const selectedClass = "selected";

const getElementToFocus = (el: HTMLElement) => {
	if (el instanceof HTMLAnchorElement || el instanceof HTMLButtonElement || el instanceof HTMLInputElement) {
		return el;
	} else {
		return expressQuerySelector<HTMLElement>(el, "a, button, input") || el;
	}
};

export interface ICreateDropDownKeyboardNavigatorDependencies {
	onEnter: (e: KeyboardEvent) => void;
	onEscape: (e: KeyboardEvent) => void;
	onArrowUp?: (e: KeyboardEvent) => void;
	onBeforeArrowUp?: () => void;
	onArrowDown?: (e: KeyboardEvent) => void;
	onBeforeArrowDown?: () => void;
	dropdownItemEls?: HTMLElement[];
}

export function createDropDownKeyboardNavigator(
	containerEl: HTMLElement,
	deps: ICreateDropDownKeyboardNavigatorDependencies,
): IDisposable {
	const onArrowDown = (e: KeyboardEvent) => {
		e.preventDefault();
		if (deps.onBeforeArrowDown) {
			closeOtherDropDowns();
			deps.onBeforeArrowDown();
		}
		if (!deps.dropdownItemEls || deps.dropdownItemEls.length === 0) return;
		const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		if (focusedEl) {
			expressRemoveClass(focusedEl, selectedClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(deps.dropdownItemEls, focusedEl);
			const nextEl = deps.dropdownItemEls[indexOfFocusedEl + 1];
			if (nextEl) {
				getElementToFocus(nextEl).focus();
				expressAddClass(nextEl, selectedClass);
			} else {
				getElementToFocus(deps.dropdownItemEls[0]).focus();
				expressAddClass(deps.dropdownItemEls[0], selectedClass);
			}
		} else {
			getElementToFocus(deps.dropdownItemEls[0]).focus();
			expressAddClass(deps.dropdownItemEls[0], selectedClass);
		}
	};

	const onArrowUp = (e: KeyboardEvent) => {
		e.preventDefault();
		if (deps.onBeforeArrowUp) deps.onBeforeArrowUp();
		if (!deps.dropdownItemEls || deps.dropdownItemEls.length === 0) return;
		const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		if (focusedEl) {
			expressRemoveClass(focusedEl, selectedClass);
			const indexOfFocusedEl = Array.prototype.indexOf.call(deps.dropdownItemEls, focusedEl);
			const prevEl = deps.dropdownItemEls[indexOfFocusedEl - 1];
			if (prevEl) {
				getElementToFocus(prevEl).focus();
				expressAddClass(prevEl, selectedClass);
			} else {
				getElementToFocus(deps.dropdownItemEls[deps.dropdownItemEls.length - 1]).focus();
				expressAddClass(deps.dropdownItemEls[deps.dropdownItemEls.length - 1], selectedClass);
			}
		} else {
			getElementToFocus(deps.dropdownItemEls[deps.dropdownItemEls.length - 1]).focus();
			expressAddClass(deps.dropdownItemEls[deps.dropdownItemEls.length - 1], selectedClass);
		}
	};

	const onEnter = (e: KeyboardEvent) => {
		const currentItem = e.currentTarget as HTMLElement;
		const openLanguageEl = hasClass(currentItem, "a-language-selector--open");
		const openDropDownEl = expressQuerySelector(currentItem, ".menu-is-open");
		if (openLanguageEl || openDropDownEl) {
			onEscape(e);
		} else {
			closeOtherDropDowns();
			const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
			if (focusedEl) expressRemoveClass(focusedEl, selectedClass);
			deps.onEnter(e);
		}
	};

	const onEscape = (e: KeyboardEvent) => {
		const focusedEl = deps.dropdownItemEls.find((el) => hasClass(el, selectedClass));
		if (focusedEl) expressRemoveClass(focusedEl, selectedClass);
		deps.onEscape(e);
	};

	const onKeyDown = (e: KeyboardEvent) => {
		if (e.key === "Enter") onEnter(e);
		if (e.key === "Escape") onEscape(e);
		if (e.key === "ArrowUp" && (deps.dropdownItemEls || deps.onArrowUp))
			deps.dropdownItemEls ? onArrowUp(e) : deps.onArrowUp(e);
		if (e.key === "ArrowDown" && (deps.dropdownItemEls || deps.onArrowDown))
			deps.dropdownItemEls ? onArrowDown(e) : deps.onArrowDown(e);
	};

	const closeOtherDropDowns = () => {
		const openDropdowns = expressQuerySelectorAll<HTMLElement>(document, ".menu-is-open, .a-language-selector--open");
		openDropdowns.forEach((dropdown) => {
			expressRemoveClass(dropdown, "menu-is-open");
			expressRemoveClass(dropdown, "a-language-selector--open");
		});
	};

	return compositeDisposable([expressEventListener(containerEl, "keydown", onKeyDown)]);
}
